/**
 * contracts-v2 (WP-23): accounts, orgs and the credentials an account issues.
 *
 * One account per person is the only root of trust. Accounts belong to orgs; repos belong to orgs. Every
 * Durable Object, Artifacts repo, event, deploy record and API call is scoped to one org, and a caller outside
 * that org gets a 404 for anything in it.
 *
 * The accounts Worker (`apps/accounts`) is the only place credentials are issued or checked. Other Workers reach
 * it over a service binding (`ACCOUNTS`) that implements {@link AccountsService}, and ask it about every bearer
 * they are handed, on every use, so a revoke in account settings cuts a credential off on its next use.
 *
 * Naming rules here are the only place org, repo and fork names are built or parsed. Never build them by hand.
 */
import { z } from "zod";
import { WpIdSchema } from "./primitives.ts";

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/**
 * Org slug: lowercase letters, digits and single hyphens, 1 to 39 characters, starting and ending with a letter
 * or digit. Immutable once created (it keys Durable Objects and Artifacts repos). No `--`, because `--`
 * separates org, repo and fork in Artifacts repo names.
 */
export const OrgSlugSchema = z
  .string()
  .min(1)
  .max(39)
  .regex(/^[a-z0-9](?:-?[a-z0-9])*$/, "expected lowercase letters, digits and single hyphens, for example acme or acme-labs");
/** See {@link OrgSlugSchema}. */
export type OrgSlug = z.infer<typeof OrgSlugSchema>;

/**
 * Repo name within an org: letters, digits, `.`, `_` and single hyphens, 1 to 50 characters, starting with a
 * letter or digit. No `--` (see {@link OrgSlugSchema}).
 */
export const RepoNameSchema = z
  .string()
  .min(1)
  .max(50)
  .regex(/^[A-Za-z0-9](?:[A-Za-z0-9._]|-(?!-))*$/, "expected letters, digits, '.', '_' and single hyphens, for example ladder");
/** See {@link RepoNameSchema}. */
export type RepoName = z.infer<typeof RepoNameSchema>;

/** Account id: `acc_` and 16 to 40 letters or digits. Opaque. */
export const AccountIdSchema = z.string().regex(/^acc_[A-Za-z0-9]{16,40}$/, "expected acc_<id>");
/** See {@link AccountIdSchema}. */
export type AccountId = z.infer<typeof AccountIdSchema>;

/** One repo, in one org. */
export const RepoRefSchema = z.object({ org: OrgSlugSchema, repo: RepoNameSchema });
/** See {@link RepoRefSchema}. */
export type RepoRef = z.infer<typeof RepoRefSchema>;

/** The Durable Object name for anything kept per repo (coordinator, planner): `<org>/<repo>`. */
export function repoKey(ref: RepoRef): string {
  return `${ref.org}/${ref.repo}`;
}

/** Path prefix every org-scoped HTTP API uses for a repo: `/orgs/<org>/repos/<repo>`. */
export function repoPath(ref: RepoRef): string {
  return `/orgs/${ref.org}/repos/${ref.repo}`;
}

/** The repo's Artifacts repo name: `<org>--<repo>`. */
export function artifactsRepoName(ref: RepoRef): string {
  return `${ref.org}--${ref.repo}`;
}

/**
 * A fork's Artifacts repo name: `<org>--<repo>--<wp>-<suffix>`, for example `acme--ladder--wp-03-alpha`.
 * `suffix` is lowercased and anything outside `[a-z0-9]` becomes `-`.
 */
export function forkRepoName(ref: RepoRef, wpId: string, suffix: string): string {
  const clean = suffix
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24) || "fork";
  return `${artifactsRepoName(ref)}--${WpIdSchema.parse(wpId).toLowerCase()}-${clean}`;
}

/** What an Artifacts repo name says: the repo it belongs to, and the fork part if it is a fork. */
export interface ParsedRepoName extends RepoRef {
  /** For a fork, the work package and suffix. Null for the repo itself. */
  fork: { wpId: string; suffix: string } | null;
}

/** Parse an Artifacts repo name built by {@link artifactsRepoName} or {@link forkRepoName}. Null for anything else. */
export function parseArtifactsRepoName(name: string): ParsedRepoName | null {
  const parts = name.split("--");
  if (parts.length < 2 || parts.length > 3) return null;
  const ref = RepoRefSchema.safeParse({ org: parts[0], repo: parts[1] });
  if (!ref.success) return null;
  if (parts.length === 2) return { ...ref.data, fork: null };
  const m = /^wp-(\d{2,3})-([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)$/.exec(parts[2]!);
  return m ? { ...ref.data, fork: { wpId: `WP-${m[1]}`, suffix: m[2]! } } : null;
}

/** The principal an account approves and acts as: `human:<accountId>`. */
export function accountPrincipal(accountId: AccountId): string {
  return `human:${accountId}`;
}

/** The account a `human:<accountId>` principal names, or null. */
export function accountOfPrincipal(principal: string): AccountId | null {
  const id = principal.startsWith("human:") ? principal.slice("human:".length) : "";
  return AccountIdSchema.safeParse(id).success ? id : null;
}

// ---------------------------------------------------------------------------
// Credentials
// ---------------------------------------------------------------------------

/**
 * Every kind of credential an account issues.
 * - `session`: signed in to the app (GitHub or email link). The only kind that may approve.
 * - `cli`: issued to `botallion login`; drives the Git credential helper and the CLI.
 * - `pat`: a personal access token the account created in settings, with chosen scopes and expiry.
 * - `seat_key`: a headless runner's key, scoped to one repo and one work package.
 * - `oauth`: an MCP client (Claude Code and others) connected through OAuth, scoped to one seat.
 */
export const CredentialKindSchema = z.enum(["session", "cli", "pat", "seat_key", "oauth"]);
/** See {@link CredentialKindSchema}. */
export type CredentialKind = z.infer<typeof CredentialKindSchema>;

/**
 * What a credential may do.
 * - `read`: read org and repo state (plans, runs, events, blame).
 * - `write`: start and change things (specs, plans, dispatch, repos), never approve.
 * - `git:read` / `git:write`: clone, fetch and push on git.botallion.ai.
 * - `seats`: mint seat keys and offer seats for OAuth.
 * - `approve`: approve plans and promotions and settle escalations. App sessions only.
 * - `seat`: act as the one seat the credential is bound to, through the MCP server.
 */
export const CredentialScopeSchema = z.enum(["read", "write", "git:read", "git:write", "seats", "approve", "seat"]);
/** See {@link CredentialScopeSchema}. */
export type CredentialScope = z.infer<typeof CredentialScopeSchema>;

/** Scopes each kind gets. A PAT gets the subset its creator picked, never `approve` or `seat`. */
export const CREDENTIAL_SCOPES: Record<CredentialKind, readonly CredentialScope[]> = {
  session: ["read", "write", "git:read", "git:write", "seats", "approve"],
  cli: ["read", "write", "git:read", "git:write", "seats"],
  pat: ["read", "write", "git:read", "git:write", "seats"],
  seat_key: ["seat"],
  oauth: ["seat"],
};

/**
 * Bearer prefixes, so a Worker can tell an account credential from its own internal tokens without a lookup.
 * Session tokens are the app's cookie value, prefixed when sent as a bearer by a server-side proxy.
 */
export const CREDENTIAL_PREFIX = { session: "bas_", cli: "bac_", pat: "bap_", seat_key: "bsk_" } as const;

/** True when a bearer looks like an account-issued credential (any kind but OAuth, which the MCP server holds). */
export function isAccountCredential(token: string): boolean {
  return Object.values(CREDENTIAL_PREFIX).some((p) => token.startsWith(p));
}

/** The one seat a seat key or OAuth connection acts as: one agent on one work package in one repo. */
export const SeatGrantSchema = z.object({
  /** Seat id. Names the MCP seat Durable Object. */
  seatId: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
  org: OrgSlugSchema,
  repo: RepoNameSchema,
  wpId: WpIdSchema,
  agentName: z.string().min(1).max(64),
  forkRepo: z.string().min(1).max(200),
  forkRemote: z.string().min(1).max(500),
  vendor: z.string().min(1).max(64).optional(),
  /** Issued at, epoch seconds. */
  iat: z.number().int(),
  /** Expires at, epoch seconds. */
  exp: z.number().int(),
});
/** See {@link SeatGrantSchema}. */
export type SeatGrant = z.infer<typeof SeatGrantSchema>;

/** Org roles. Owners manage members, invites and Connect Cloudflare; members do everything else. */
export const OrgRoleSchema = z.enum(["owner", "member"]);
/** See {@link OrgRoleSchema}. */
export type OrgRole = z.infer<typeof OrgRoleSchema>;

/** Who a credential belongs to and what it may do. Returned by {@link AccountsService.authenticate}. */
export const AuthContextSchema = z.object({
  account: z.object({
    id: AccountIdSchema,
    email: z.string().nullable(),
    name: z.string().nullable(),
    githubLogin: z.string().nullable(),
  }),
  credential: z.object({
    /** The credential's id, as account settings lists it. Recorded wherever an action is traced back. */
    id: z.string().min(1),
    kind: CredentialKindSchema,
    scopes: z.array(CredentialScopeSchema),
    /** ISO 8601, or null for a credential that only ends when revoked or signed out. */
    expiresAt: z.string().nullable(),
  }),
  /** Every org the account belongs to. A seat-bound credential sees only its seat's org. */
  memberships: z.array(z.object({ org: OrgSlugSchema, role: OrgRoleSchema })),
  /** For `seat_key` and `oauth`: the seat. Null otherwise. */
  seat: SeatGrantSchema.nullable(),
});
/** See {@link AuthContextSchema}. */
export type AuthContext = z.infer<typeof AuthContextSchema>;

/** What a caller is asking to do, for {@link AccountsService.authorize}. */
export interface AccessRequest {
  org: OrgSlug;
  /** When set, a seat-bound credential must be bound to this repo. */
  repo?: RepoName;
  /** The scope the action needs. */
  need: CredentialScope;
}

/**
 * The answer to {@link AccountsService.authorize}. The status is what the calling Worker returns:
 * - 401: no such credential, or it was revoked or has expired.
 * - 404: the credential is live, but the org (or, for a seat, the repo) is not one it can see. Never 403, so
 *   nothing reveals whether another org's repo exists.
 * - 403: it can see the org but lacks the scope (for example a PAT trying to approve).
 */
export type Access =
  | { ok: true; ctx: AuthContext; /** `human:<accountId>`. */ principal: string }
  | { ok: false; status: 401 | 403 | 404; message: string };

/** Body for minting a seat key or offering a seat for OAuth. */
export const SeatRequestSchema = z.object({
  org: OrgSlugSchema,
  repo: RepoNameSchema,
  wpId: WpIdSchema,
  agentName: z.string().min(1).max(64),
  forkRepo: z.string().min(1).max(200),
  forkRemote: z.string().min(1).max(500),
  vendor: z.string().min(1).max(64).optional(),
  /** Hours, at most 14 days. Default 7 days. */
  ttlHours: z.number().positive().max(14 * 24).optional(),
});
/** See {@link SeatRequestSchema}. */
export type SeatRequest = z.infer<typeof SeatRequestSchema>;

/** Seat lifetimes. */
export const SEAT_TTL = { defaultHours: 7 * 24, maxHours: 14 * 24 } as const;

/** A failure from an {@link AccountsService} write, with the status the calling Worker returns. */
export type AccountsFailure = { ok: false; status: 400 | 401 | 403 | 404 | 409; message: string };

/** A completed MCP OAuth consent, as the MCP server redeems it. */
export interface ConsentGrant {
  accountId: AccountId;
  seat: SeatGrant;
}

/** An MCP OAuth connection, recorded in the account's settings. */
export interface OAuthConnectionInput {
  connectionId: string;
  accountId: AccountId;
  clientId: string;
  clientName: string;
  redirectHost: string;
  seat: SeatGrant;
}

/**
 * The accounts Worker's interface for other Botallion Workers, over the `ACCOUNTS` service binding
 * (`WorkerEntrypoint` RPC). Writes that act for an account take that account's own bearer, so no Worker can
 * act for an account without its credential.
 */
export interface AccountsService {
  /** Who a bearer belongs to, or null when it is unknown, revoked or expired. Records last use. */
  authenticate(token: string): Promise<AuthContext | null>;
  /** {@link authenticate}, then check org membership (or seat binding) and scope. See {@link Access}. */
  authorize(token: string, request: AccessRequest): Promise<Access>;
  /** Mint a seat key for a headless runner. Needs `seats` in the org. The key is returned once. */
  mintSeatKey(token: string, request: SeatRequest): Promise<{ ok: true; key: string; seat: SeatGrant } | AccountsFailure>;
  /** Offer a seat for an MCP client to take through OAuth consent. Needs `seats` in the org. */
  offerSeat(token: string, request: SeatRequest): Promise<{ ok: true; seat: SeatGrant } | AccountsFailure>;
  /**
   * Redeem the single-use ticket the app's consent page hands back to the MCP server, for the consent `handle` it was
   * issued for. Null if used, lapsed, or issued for another handle.
   */
  redeemConsent(ticket: string, handle: string): Promise<ConsentGrant | null>;
  /** Record an MCP OAuth connection after its grant is stored. */
  recordOAuthConnection(input: OAuthConnectionInput): Promise<void>;
  /** Remember the OAuth provider's grant id for a connection. */
  bindOAuthGrant(connectionId: string, grantId: string): Promise<void>;
  /** May this OAuth connection act now? False once revoked in settings, signed out everywhere, or expired. */
  checkOAuthConnection(connectionId: string): Promise<boolean>;
}

/**
 * An org's Connect Cloudflare connection (contracts-v3, WP-30): the token when it has a usable one, or why it has
 * none. Tokens appear only in `ok`.
 * - `none`: never connected, or disconnected. `needs_account`: consented, but no Cloudflare account picked yet.
 * - `revoked`: Cloudflare refused the refresh (revoked on its Connected Applications page, or lapsed). Reconnect.
 * - `unavailable`: Cloudflare, or this install's OAuth client, could not be reached. Try again; not a revocation.
 *
 * `scopes` are the OAuth scopes granted; `artifacts` says whether the org's repos live in Botallion's Artifacts
 * (`hosted`) or the org's own account (`own`, in `namespace`).
 */
export type CloudflareAccess =
  | { state: "ok"; accountId: string; apiToken: string; scopes: string[]; artifacts: { mode: "hosted" | "own"; namespace: string | null } }
  | { state: "none"; message: string }
  | { state: "needs_account"; message: string }
  | { state: "revoked"; message: string }
  | { state: "unavailable"; message: string };

/**
 * The deployer's own entrypoint on the accounts Worker (`ACCOUNTS_DEPLOYER` binding, entrypoint
 * `DeployerGrants`). Only the deployer binds it. Returns an org's Connect Cloudflare grant, decrypted.
 */
export interface DeployerGrants {
  /** The usable grant, or null for any reason there isn't one. {@link cloudflareAccess} says which reason. */
  cloudflareGrant(org: OrgSlug): Promise<{ accountId: string; apiToken: string } | null>;
  /**
   * The org's connection, with the reason when it can't be used (contracts-v3). `fresh`: ask Cloudflare to refresh
   * first, so a grant revoked upstream is refused now and not when the current access token lapses.
   */
  cloudflareAccess(org: OrgSlug, opts?: { fresh?: boolean }): Promise<CloudflareAccess>;
}

/**
 * Where an org's repos live, for the events Worker (contracts-v3, WP-30). `hosted` (Botallion's own Artifacts) carries
 * no token. An org on its own Artifacts gets its account, namespace and a live token, or `ok: false` with why
 * (revoked, unreachable): events must then stop for that org, never fall back to Botallion's namespace.
 */
export type ArtifactsRoute =
  | { mode: "hosted" }
  | { mode: "own"; ok: true; accountId: string; apiToken: string; namespace: string }
  | { mode: "own"; ok: false; message: string };

/**
 * Events' own entrypoint on the accounts Worker (`ACCOUNTS_ARTIFACTS` binding, entrypoint `ArtifactsGrants`), so
 * the deployer's entrypoint stays deployer-only. Only the events Worker binds it.
 */
export interface ArtifactsGrants {
  /** `fresh`: ask Cloudflare to refresh first, as for {@link DeployerGrants.cloudflareAccess}. */
  artifactsRoute(org: OrgSlug, opts?: { fresh?: boolean }): Promise<ArtifactsRoute>;
}

// ---------------------------------------------------------------------------
// The app session cookie
// ---------------------------------------------------------------------------

/**
 * The app's session cookie names, set host-only on app.botallion.ai: the `__Secure-` one over HTTPS, the plain one
 * on local http.
 */
export const SESSION_COOKIE_NAMES = ["__Secure-botallion.session_token", "botallion.session_token"] as const;

/**
 * The bearer (`bas_<token>`) for the app session in a Cookie header, or null. For Workers on the app host (the
 * dashboard) that call other Workers for the signed-in person. The cookie value is `<token>.<signature>`; only
 * the token is sent on, and the accounts Worker looks it up, so a forged or signed-out cookie gets a 401.
 *
 * `secureOnly` (contracts-v3) reads only the `__Secure-` name. Pass it for every https request:
 * `{ secureOnly: new URL(request.url).protocol === "https:" }`. A browser never lets plain http set a `__Secure-`
 * cookie, but it stores an unprefixed one, so without this a hostile network could plant a session on a signed-out
 * person. Local http development still reads the unprefixed name.
 */
export function sessionBearerFromCookie(cookieHeader: string | null | undefined, options: { secureOnly?: boolean } = {}): string | null {
  if (!cookieHeader) return null;
  for (const name of options.secureOnly ? SESSION_COOKIE_NAMES.slice(0, 1) : SESSION_COOKIE_NAMES) {
    for (const part of cookieHeader.split(";")) {
      const i = part.indexOf("=");
      if (i < 0 || part.slice(0, i).trim() !== name) continue;
      let value = part.slice(i + 1).trim();
      try {
        value = decodeURIComponent(value);
      } catch {
        return null;
      }
      const token = value.split(".")[0] ?? "";
      if (/^[A-Za-z0-9_-]{16,128}$/.test(token)) return `${CREDENTIAL_PREFIX.session}${token}`;
    }
  }
  return null;
}

/**
 * Every registered repo, for platform sweeps that run with nobody asking (the protocol referee). Binding
 * `ACCOUNTS_REPOS`, entrypoint `RepoDirectory` on the accounts Worker. Read-only, names only.
 */
export interface RepoDirectory {
  listRepos(): Promise<RepoRef[]>;
}
